/**
 * Full-screen lightbox for image attachments with prev/next navigation.
 * Keyboard: ← / → to navigate, Esc to close.
 */
import { useCallback, useEffect } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { Close, ChevronLeft, ChevronRight, OpenInNew, Download } from '@mui/icons-material';
import type { FeedbackAttachment } from '../../types/feedback';

interface Props {
  open: boolean;
  attachments: FeedbackAttachment[];
  index: number;
  onIndexChange: (idx: number) => void;
  onClose: () => void;
}

export const AttachmentLightbox = ({ open, attachments, index, onIndexChange, onClose }: Props) => {
  const count = attachments.length;
  const current = attachments[index];

  const prev = useCallback(() => {
    if (count > 0) onIndexChange((index - 1 + count) % count);
  }, [count, index, onIndexChange]);

  const next = useCallback(() => {
    if (count > 0) onIndexChange((index + 1) % count);
  }, [count, index, onIndexChange]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, prev, next, onClose]);

  if (!current) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullScreen
      slotProps={{ paper: { sx: { bgcolor: 'rgba(0,0,0,0.92)', color: '#fff' } } }}>
      {/* Top bar */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
        display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
        background: 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)',
      }}>
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {current.FileName}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {index + 1} / {count}
        </Typography>
        {current.imageUrl && (
          <>
            <IconButton size="small" component="a" href={current.imageUrl} target="_blank" rel="noopener"
              sx={{ color: '#fff' }} title="Open in new tab">
              <OpenInNew sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton size="small" component="a" href={current.imageUrl} download={current.FileName}
              sx={{ color: '#fff' }} title="Download">
              <Download sx={{ fontSize: 18 }} />
            </IconButton>
          </>
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }} title="Close (Esc)">
          <Close />
        </IconButton>
      </Box>

      {/* Image area */}
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 6 }}>
        {current.imageUrl ? (
          <Box component="img" src={current.imageUrl} alt={current.FileName}
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 1 }} />
        ) : (
          <Typography color="rgba(255,255,255,0.7)">No preview available</Typography>
        )}
      </Box>

      {/* Nav arrows */}
      {count > 1 && (
        <>
          <IconButton onClick={prev} sx={{
            position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
            color: '#fff', bgcolor: 'rgba(0,0,0,0.4)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
            width: 48, height: 48,
          }} title="Previous (←)">
            <ChevronLeft sx={{ fontSize: 32 }} />
          </IconButton>
          <IconButton onClick={next} sx={{
            position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
            color: '#fff', bgcolor: 'rgba(0,0,0,0.4)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
            width: 48, height: 48,
          }} title="Next (→)">
            <ChevronRight sx={{ fontSize: 32 }} />
          </IconButton>
        </>
      )}

      {/* Thumbnail strip at bottom */}
      {count > 1 && (
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
          display: 'flex', gap: 0.75, justifyContent: 'center', alignItems: 'center',
          px: 2, py: 1.5, overflow: 'auto',
          background: 'linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
        }}>
          {attachments.map((a, i) => (
            a.imageUrl ? (
              <Box key={a.FeedbackAttachmentKey} component="img" src={a.imageUrl}
                onClick={() => onIndexChange(i)}
                sx={{
                  width: 48, height: 48, objectFit: 'cover', borderRadius: 0.5,
                  cursor: 'pointer', flexShrink: 0,
                  border: i === index ? '2px solid #fff' : '2px solid transparent',
                  opacity: i === index ? 1 : 0.6,
                  '&:hover': { opacity: 1 },
                }} />
            ) : null
          ))}
        </Box>
      )}
    </Dialog>
  );
};
